/** @prettier */

import {Grid, Box, Container} from '@mui/material';
import {useEffect, useState} from 'react';
import {
  useLocation,
  useLoaderData,
  useParams,
  Outlet,
  useOutletContext,
} from 'react-router-dom';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import GroupSettingTreeView from '../../component/GroupSettingTreeView';
import RegistrationBreadcrumbs from '../../component/RegistrationBreadcrumbs';
//API import
import {
  getCompositionsGroups,
  getCompositionsSitesBySiteID,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {sortByUnicode} from '../../functions/sort';
import {limit} from '../../define';
import {checkUnitExistence} from '../../functions/checkUnitExistence';
import type {unitExistence} from '../../functions/checkUnitExistence';

type OutletContextType = {
  floorName: string | null;
  allGroupInfo: apiIF.res.getCompositionsGroups | null;
  setAllGroupInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsGroups | null>
  >;
};

type loaderReturn = apiIF.res.getCompositionsGroups;
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<loaderReturn | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }

  try {
    const promises = [];
    //サイトに登録されているすべてのグループの情報を取得
    promises.push(
      getCompositionsGroups(params.siteID, {
        'max-result': limit.REGISTRATION_NUM_GROUP_MAX,
      })
    );
    //サイトの情報を取得
    promises.push(getCompositionsSitesBySiteID(params.siteID));
    //全てのユニットの登録状況を取得
    promises.push(checkUnitExistence(params.siteID, true));
    const result = await Promise.all(promises);

    const groupInfo = result[0] as apiIF.res.getCompositionsGroups;
    const siteInfo = result[1] as apiIF.res.getCompositionsSitesBySiteID;
    const unitExistence = result[2] as unitExistence;

    if (
      unitExistence.hasAirconIU === false ||
      siteInfo['site-authority'] !== 'admin'
    ) {
      throw new Error(
        'Required equipment not registered or insufficient permission'
      );
    }

    //表示内容をUnicode順に並び替え
    groupInfo['group-informations'].sort(sortByUnicode('group-name'));
    for (const info of groupInfo['group-informations']) {
      if (info['unit-informations'] === undefined) continue;
      info['unit-informations'].sort(sortByUnicode('unit-name'));
    }

    return groupInfo;
  } catch {
    return undefined;
  }
}

/**
 * グループ一覧メイン画面
 * @returns JSX.Element
 */
const FloorMain = () => {
  const trans = useTranslation().t; //翻訳関数
  const i18n = useTranslation().i18n;
  const loaderData = useLoaderData() as loaderReturn | undefined;
  //URL取得
  const location = useLocation();
  //パスパラメータ取得
  const {floorID, groupID} = useParams();

  const [floorName, setFloorName] = useState<string | null>(null); //表示しているフロアの名前
  const [allGroupInfo, setAllGroupInfo] =
    useState<apiIF.res.getCompositionsGroups | null>(null); //サイトに登録されているすべてのグループの情報
  const [displayGroupInfo, setDisplayGroupInfo] =
    useState<apiIF.resParameters.groupInformations | null>(null); //表示しているグループの情報
  const [defaultExpanded, setDefaultExpanded] = useState<string[]>(['none']); //ツリービューで初期に開く階層を指定（nodeIDで指定）

  useEffect(() => {
    if (loaderData !== undefined) {
      setAllGroupInfo(loaderData);
    }
  }, [loaderData]);

  useEffect(() => {
    //ツリービューで初期に開く階層を決定
    if (
      floorID !== undefined &&
      location.pathname.endsWith('group-registration')
    ) {
      //グループ一覧画面
      const temp =
        Number(floorID) < 0
          ? trans(`FloorMain.floor.basement${Math.abs(Number(floorID))}`)
          : trans(`FloorMain.floor.floor${floorID}`);
      setFloorName(temp);
      setDefaultExpanded([floorID]);
    } else if (
      floorID !== undefined &&
      groupID !== undefined &&
      location.pathname.endsWith('group-layout-registration')
    ) {
      //グループレイアウト画面
      const temp =
        Number(floorID) < 0
          ? trans(`FloorMain.floor.basement${Math.abs(Number(floorID))}`)
          : trans(`FloorMain.floor.floor${floorID}`);
      setFloorName(temp);
      setDefaultExpanded([floorID, groupID]);
    } else {
      //フロア一覧画面
      setDefaultExpanded(['none']);
    }

    //表示しているグループの情報を取得
    if (groupID !== undefined && allGroupInfo !== null) {
      const temp = allGroupInfo['group-informations'].find(
        groupInfo => groupInfo['group-id'] === groupID
      );
      if (temp !== undefined) {
        setDisplayGroupInfo(temp);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, allGroupInfo, i18n.language]);

  return (
    <Box className="groupSetting">
      <Grid container>
        {/** ツリービュー */}
        {/** lgより小さい画面では非表示 */}
        <Grid item lg={2.5} sx={{display: {xs: 'none', lg: 'block'}}}>
          <GroupSettingTreeView
            defaultExpanded={defaultExpanded}
            groupInfoData={allGroupInfo}
            unitSystem="sl"
          />
        </Grid>
        <Grid item xs={12} lg={9.5}>
          <Container fixed>
            {/* パンくずリスト */}
            <RegistrationBreadcrumbs
              groupName={
                displayGroupInfo === null
                  ? undefined
                  : displayGroupInfo['group-name']
              }
            />
            <Outlet
              context={{
                floorName: floorName,
                allGroupInfo: allGroupInfo,
                setAllGroupInfo: setAllGroupInfo,
              }}
            />
          </Container>
        </Grid>
      </Grid>
    </Box>
  );
};
const useOutlet = () => useOutletContext<OutletContextType>();

export {useOutlet};
export default FloorMain;
